import type {
  MetricKey,
  PerformanceReportTableData,
  PerformanceReportTableParams,
  SourceType,
  SummaryCard,
  ViewTab,
} from "../types";

import {
  createReportContext,
  type InferReport,
} from "../../context/ReportContext";

/**
 * The "view by" tab behaves exactly like a filter facet from the engine's
 * perspective (drives deriveParams -> triggers the table resource's
 * shouldAutoRun), so it's modeled as a single-mode select alongside the
 * five real filter facets.
 *
 * createReportContext's TParams generic requires a `selected` field (it's
 * the generic engine's own bookkeeping shape) that the public
 * PerformanceReportTableParams type doesn't carry. PerformanceReportParams
 * extends it with that field for internal use only.
 */
export type PerformanceReportParams = PerformanceReportTableParams & {
  selected: {
    viewTab: ViewTab | null;
    sourceType: SourceType[] | null;
    landingPage: string[] | null;
    bannerGroup: string[] | null;
    rebateProgram: string[] | null;
    country: string[] | null;
    campaign: string[] | null;
  };
  /**
   * Visible metric-column keys in funnel order, derived from the column
   * configurator (`view.hidden`). Consumed by the CSV export resources; the
   * table resource ignores it (and its `shouldAutoRun` does not compare it, so
   * toggling a column never refetches the table).
   */
  columns: MetricKey[];
  /**
   * Derived CSV filename (without extension), e.g.
   * `Performance-source-13-Jul-2026-12-Aug-2026`.
   */
  fileName: string;
};

/**
 * Page-only, client-side view state carried by the shared report engine's
 * generic `view` slot (see ReportState.view). None of it drives data fetching:
 *  - `hidden`  — column-visibility map for the column configurator
 *
 * Sorting is NOT here: it is server-side and lives in the engine's built-in
 * `sortBy`/`sortOrder` state, mapped to `sortColumn`/`sortDesc` in deriveParams.
 */
export type PerformanceReportView = {
  hidden: Partial<Record<MetricKey, boolean>>;
};

const performanceReport = createReportContext({
  viewTab: "single",
  sourceType: "multi",
  landingPage: "multi",
  bannerGroup: "multi",
  rebateProgram: "multi",
  country: "multi",
  campaign: "multi",
})<
  {
    viewTab: ViewTab;
    sourceType: SourceType;
    landingPage: string;
    bannerGroup: string;
    rebateProgram: string;
    country: string;
    campaign: string;
  },
  // `csv` (visible columns) and `csvAll` (every column) are MANUAL export
  // resources run via `runResource`; both carry no payload (`void`) since the
  // injected handler owns the blob download. `stats` (the headline KPI cards)
  // is a separate AUTO resource so it only reloads on date-range / filter
  // changes, decoupled from table interactions.
  {
    table: PerformanceReportTableData;
    stats: SummaryCard[];
    csv: void;
    csvAll: void;
  },
  PerformanceReportParams,
  PerformanceReportView
>();

export const {
  Provider: BasePerformanceReportProvider,
  useReportContext: useBasePerformanceReportContext,
} = performanceReport;

export type PerformanceReportStates = InferReport<
  typeof performanceReport,
  "states"
>;
